/**
 * Заметки: список, заметка, участники, напоминания, файлы и ссылки.
 *
 * Список и открытая заметка — разные теги. Автосохранение меняет заметку
 * часто, а список при этом нужен лишь ради фрагмента и даты: он
 * обновляется событием note.listChanged, а не после каждой мутации.
 */

import type { NoteMemberStatus, NoteRole, NoteSort } from "@taskflow/contracts";

import { api } from "../api";

import type { NoteBlock } from "./noteBlock";

export interface NoteListItem {
  id: string;
  title: string;
  excerpt: string;
  editedAt: string;
  createdAt: string;
  pinned: boolean;
  role: NoteRole;
  ownerName: string | null;
  archived: boolean;
  /** Есть приглашённые участники. */
  shared: boolean;
  /** Ближайшее невыполненное напоминание — своё. */
  nextReminderAt: string | null;
  /**
   * Секция, где заметка в списке: своя, куда человек её положил, или
   * общая чужая, через которую она открыта; null — общий блок.
   */
  sectionId: string | null;
  /** Место в секции по ручному порядку; null — не расставлена. */
  sectionPosition: number | null;
}

/** Секция списка заметок: своя (role OWNER) или общая чужая. */
export interface NoteSection {
  id: string;
  name: string;
  role: NoteRole;
  /** Хозяин чужой секции; у своей — null. */
  ownerName: string | null;
  /** Сколько человек приглашено в секцию. */
  memberCount: number;
}

export interface NoteSectionDetail extends NoteSection {
  /** Хозяин первым, затем приглашённые. */
  members: NoteMember[];
}

export interface NoteSectionInvitation {
  sectionId: string;
  name: string;
  role: Exclude<NoteRole, "OWNER">;
  invitedByName: string | null;
  invitedAt: string;
}

export interface NoteInvitation {
  noteId: string;
  title: string;
  role: Exclude<NoteRole, "OWNER">;
  invitedByName: string | null;
  invitedAt: string;
}

export interface NoteListPage {
  own: NoteListItem[];
  shared: NoteListItem[];
  invitations: NoteInvitation[];
  /** Ждущие приглашения в чужие секции. */
  sectionInvitations: NoteSectionInvitation[];
  /** Свои секции и принятые общие — в выбранном человеком порядке. */
  sections: NoteSection[];
}

export interface NoteListQuery {
  archived?: boolean;
  search?: string;
  sort?: NoteSort;
}

export interface NoteMember {
  userId: string;
  name: string | null;
  avatarUrl: string | null;
  role: NoteRole;
  status: NoteMemberStatus;
}

export interface NoteAttachment {
  id: string;
  filename: string;
  mime: string;
  size: number;
  sizeLabel: string;
  uploadedByName: string | null;
  uploadedAt: string;
  canDelete: boolean;
}

export interface NoteLink {
  id: string;
  url: string;
  title: string | null;
  createdByName: string | null;
  canDelete: boolean;
}

export interface NoteReminder {
  id: string;
  noteId: string;
  /** Пункт чек-листа; null — напоминание о заметке целиком. */
  itemId: string | null;
  itemText: string | null;
  remindAt: string;
  /** Уведомление уже пришло. */
  fired: boolean;
  done: boolean;
}

export interface NoteDetail {
  id: string;
  title: string;
  version: number;
  role: NoteRole;
  /** Приглашение ещё не принято: содержимого нет. */
  pending: boolean;
  canEdit: boolean;
  archived: boolean;
  pinned: boolean;
  ownerId: string;
  ownerName: string | null;
  createdAt: string;
  editedAt: string;
  updatedByName: string | null;
  /** Документ BlockNote; пустая заметка — null. */
  bodyBlocks: NoteBlock[] | null;
  members: NoteMember[];
  /** Общая секция автора, где лежит заметка: её участники тоже видят заметку. */
  sharedSection: {
    id: string;
    name: string;
    members: { userId: string; name: string | null; role: Exclude<NoteRole, "OWNER"> }[];
  } | null;
  attachments: NoteAttachment[];
  links: NoteLink[];
  reminders: NoteReminder[];
}

export interface NoteSaved {
  id: string;
  version: number;
  title: string;
  editedAt: string;
}

/**
 * Правка всех закэшированных списков заметок сразу; не сохранилось —
 * правка откатывается (сообщение показывает вызывающий код).
 */
async function optimisticList(
  // lifecycleApi целиком, а не деструктурированный: getState — метод.
  lifecycleApi: {
    getState: () => unknown;
    dispatch: (action: unknown) => unknown;
    queryFulfilled: Promise<unknown>;
  },
  change: (draft: NoteListPage) => void,
): Promise<void> {
  const state = lifecycleApi.getState() as Parameters<typeof notesApi.util.selectInvalidatedBy>[0];
  const patches = notesApi.util
    .selectInvalidatedBy(state, ["NoteList"])
    .filter((entry) => entry.endpointName === "notes")
    .map(
      (entry) =>
        lifecycleApi.dispatch(
          notesApi.util.updateQueryData("notes", entry.originalArgs as NoteListQuery, change),
        ) as { undo: () => void },
    );
  try {
    await lifecycleApi.queryFulfilled;
  } catch {
    for (const patch of patches) patch.undo();
  }
}

export const notesApi = api.injectEndpoints({
  endpoints: (build) => ({
    notes: build.query<NoteListPage, NoteListQuery>({
      query: (params) => ({ method: "notes.list", params }),
      providesTags: ["NoteList"],
    }),

    note: build.query<NoteDetail, string>({
      query: (id) => ({ method: "notes.get", params: { id } }),
      providesTags: (_result, _error, id) => [{ type: "Note", id }, "Note"],
    }),

    createNote: build.mutation<NoteDetail, { title?: string; bodyBlocks?: NoteBlock[] | null }>({
      query: (params) => ({ method: "notes.create", params }),
      invalidatesTags: ["NoteList"],
    }),

    // Без инвалидации: сохраняющий уже знает содержимое, а лишний
    // перезапрос заметки после каждой паузы в наборе сбивал бы курсор.
    updateNote: build.mutation<
      NoteSaved,
      { id: string; version: number; title?: string; bodyBlocks?: NoteBlock[] | null }
    >({
      query: (params) => ({ method: "notes.update", params }),
    }),

    setNotePinned: build.mutation<{ id: string; pinned: boolean }, { id: string; pinned: boolean }>({
      query: (params) => ({ method: "notes.setPinned", params }),
      invalidatesTags: (_r, _e, { id }) => ["NoteList", { type: "Note", id }],
    }),

    // Перенос между секциями и их порядок — перетаскиванием: список
    // правится сразу, иначе отпущенная строка возвращалась бы на миг назад.
    setNoteSection: build.mutation<{ id: string; sectionId: string | null }, { id: string; sectionId: string | null }>({
      query: (params) => ({ method: "notes.setSection", params }),
      invalidatesTags: ["NoteList"],
      async onQueryStarted({ id, sectionId }, lifecycleApi) {
        await optimisticList(lifecycleApi, (draft) => {
          for (const item of [...draft.own, ...draft.shared]) if (item.id === id) item.sectionId = sectionId;
        });
      },
    }),

    createNoteSection: build.mutation<NoteSection, { name: string }>({
      query: (params) => ({ method: "notes.sections.create", params }),
      invalidatesTags: ["NoteList"],
    }),

    renameNoteSection: build.mutation<NoteSection, { id: string; name: string }>({
      query: (params) => ({ method: "notes.sections.rename", params }),
      invalidatesTags: ["NoteList"],
    }),

    deleteNoteSection: build.mutation<{ id: string }, { id: string }>({
      query: (params) => ({ method: "notes.sections.delete", params }),
      invalidatesTags: ["NoteList"],
    }),

    // Порядок заметок внутри секции — тоже перетаскиванием и тоже сразу.
    reorderSectionNotes: build.mutation<{ id: string; ids: string[] }, { id: string; ids: string[] }>({
      query: (params) => ({ method: "notes.sections.reorderNotes", params }),
      invalidatesTags: ["NoteList"],
      async onQueryStarted({ id, ids }, lifecycleApi) {
        await optimisticList(lifecycleApi, (draft) => {
          for (const item of [...draft.own, ...draft.shared]) {
            if (item.sectionId !== id) continue;
            const place = ids.indexOf(item.id);
            item.sectionPosition = place < 0 ? null : place + 1;
          }
        });
      },
    }),

    noteSection: build.query<NoteSectionDetail, string>({
      query: (id) => ({ method: "notes.sections.get", params: { id } }),
      providesTags: (_result, _error, id) => [{ type: "NoteSection", id }],
    }),

    inviteToNoteSection: build.mutation<NoteSectionDetail, { id: string; userId: string; role: "VIEW" | "EDIT" }>({
      query: (params) => ({ method: "notes.sections.members.invite", params }),
      invalidatesTags: (_r, _e, { id }) => ["NoteList", { type: "NoteSection", id }],
    }),

    setNoteSectionMemberRole: build.mutation<
      NoteSectionDetail,
      { id: string; userId: string; role: "VIEW" | "EDIT" }
    >({
      query: (params) => ({ method: "notes.sections.members.setRole", params }),
      invalidatesTags: (_r, _e, { id }) => ["NoteList", "Note", { type: "NoteSection", id }],
    }),

    removeNoteSectionMember: build.mutation<{ ok: boolean }, { id: string; userId: string }>({
      query: (params) => ({ method: "notes.sections.members.remove", params }),
      invalidatesTags: (_r, _e, { id }) => ["NoteList", "Note", { type: "NoteSection", id }],
    }),

    acceptNoteSectionInvitation: build.mutation<NoteSectionDetail, { id: string }>({
      query: (params) => ({ method: "notes.sections.invitations.accept", params }),
      invalidatesTags: (_r, _e, { id }) => ["NoteList", { type: "NoteSection", id }],
    }),

    declineNoteSectionInvitation: build.mutation<{ ok: boolean }, { id: string }>({
      query: (params) => ({ method: "notes.sections.invitations.decline", params }),
      invalidatesTags: ["NoteList"],
    }),

    reorderNoteSections: build.mutation<{ sections: NoteSection[] }, { ids: string[] }>({
      query: (params) => ({ method: "notes.sections.reorder", params }),
      invalidatesTags: ["NoteList"],
      async onQueryStarted({ ids }, lifecycleApi) {
        await optimisticList(lifecycleApi, (draft) => {
          draft.sections.sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id));
        });
      },
    }),

    archiveNote: build.mutation<NoteDetail, { id: string }>({
      query: (params) => ({ method: "notes.archive", params }),
      invalidatesTags: (_r, _e, { id }) => ["NoteList", { type: "Note", id }],
    }),

    restoreNote: build.mutation<NoteDetail, { id: string }>({
      query: (params) => ({ method: "notes.restore", params }),
      invalidatesTags: (_r, _e, { id }) => ["NoteList", { type: "Note", id }],
    }),

    deleteNote: build.mutation<{ ok: boolean }, { id: string }>({
      query: (params) => ({ method: "notes.delete", params }),
      invalidatesTags: ["NoteList"],
    }),

    inviteToNote: build.mutation<NoteDetail, { id: string; userId: string; role: "VIEW" | "EDIT" }>({
      query: (params) => ({ method: "notes.members.invite", params }),
      invalidatesTags: (_r, _e, { id }) => ["NoteList", { type: "Note", id }],
    }),

    setNoteMemberRole: build.mutation<NoteDetail, { id: string; userId: string; role: "VIEW" | "EDIT" }>({
      query: (params) => ({ method: "notes.members.setRole", params }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "Note", id }],
    }),

    removeNoteMember: build.mutation<{ ok: boolean }, { id: string; userId: string }>({
      query: (params) => ({ method: "notes.members.remove", params }),
      invalidatesTags: (_r, _e, { id }) => ["NoteList", { type: "Note", id }],
    }),

    acceptNoteInvitation: build.mutation<NoteDetail, { id: string }>({
      query: (params) => ({ method: "notes.invitations.accept", params }),
      invalidatesTags: (_r, _e, { id }) => ["NoteList", { type: "Note", id }],
    }),

    declineNoteInvitation: build.mutation<{ ok: boolean }, { id: string }>({
      query: (params) => ({ method: "notes.invitations.decline", params }),
      invalidatesTags: ["NoteList"],
    }),

    setNoteReminder: build.mutation<
      NoteReminder,
      { id: string; remindAt: string; itemId?: string; reminderId?: string }
    >({
      query: (params) => ({ method: "notes.reminders.set", params }),
      invalidatesTags: (_r, _e, { id }) => ["NoteList", { type: "Note", id }],
    }),

    completeNoteReminder: build.mutation<NoteReminder, { id: string; noteId: string; done: boolean }>({
      query: ({ id, done }) => ({ method: "notes.reminders.complete", params: { id, done } }),
      invalidatesTags: (_r, _e, { noteId }) => ["NoteList", { type: "Note", id: noteId }],
    }),

    deleteNoteReminder: build.mutation<{ ok: boolean }, { id: string; noteId: string }>({
      query: ({ id }) => ({ method: "notes.reminders.delete", params: { id } }),
      invalidatesTags: (_r, _e, { noteId }) => ["NoteList", { type: "Note", id: noteId }],
    }),

    addNoteLink: build.mutation<{ id: string }, { id: string; url: string; title?: string }>({
      query: (params) => ({ method: "notes.links.add", params }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "Note", id }],
    }),

    removeNoteLink: build.mutation<{ ok: boolean }, { id: string; linkId: string }>({
      query: (params) => ({ method: "notes.links.remove", params }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "Note", id }],
    }),

    requestNoteUpload: build.mutation<
      { attachmentId: string; uploadUrl: string; maxBytes: number },
      { id: string; filename: string; mime: string; size: number }
    >({
      query: (params) => ({ method: "notes.attachments.requestUpload", params }),
    }),

    completeNoteUpload: build.mutation<{ id: string }, { id: string; noteId: string }>({
      query: ({ id }) => ({ method: "notes.attachments.complete", params: { id } }),
      invalidatesTags: (_r, _e, { noteId }) => [{ type: "Note", id: noteId }],
    }),

    noteAttachmentUrl: build.mutation<
      { url: string; filename: string },
      { id: string; action: "open" | "download" }
    >({
      query: ({ id, action }) => ({
        method: action === "open" ? "notes.attachments.openUrl" : "notes.attachments.downloadUrl",
        params: { id },
      }),
    }),

    noteExportUrl: build.mutation<{ url: string }, { id: string }>({
      query: (params) => ({ method: "notes.exportUrl", params }),
    }),

    deleteNoteAttachment: build.mutation<{ ok: boolean }, { id: string; noteId: string }>({
      query: ({ id }) => ({ method: "notes.attachments.delete", params: { id } }),
      invalidatesTags: (_r, _e, { noteId }) => [{ type: "Note", id: noteId }],
    }),
  }),
});

export const {
  useNotesQuery,
  useNoteQuery,
  useCreateNoteMutation,
  useUpdateNoteMutation,
  useSetNotePinnedMutation,
  useSetNoteSectionMutation,
  useCreateNoteSectionMutation,
  useRenameNoteSectionMutation,
  useDeleteNoteSectionMutation,
  useReorderNoteSectionsMutation,
  useReorderSectionNotesMutation,
  useNoteSectionQuery,
  useInviteToNoteSectionMutation,
  useSetNoteSectionMemberRoleMutation,
  useRemoveNoteSectionMemberMutation,
  useAcceptNoteSectionInvitationMutation,
  useDeclineNoteSectionInvitationMutation,
  useArchiveNoteMutation,
  useRestoreNoteMutation,
  useDeleteNoteMutation,
  useInviteToNoteMutation,
  useSetNoteMemberRoleMutation,
  useRemoveNoteMemberMutation,
  useAcceptNoteInvitationMutation,
  useDeclineNoteInvitationMutation,
  useSetNoteReminderMutation,
  useCompleteNoteReminderMutation,
  useDeleteNoteReminderMutation,
  useAddNoteLinkMutation,
  useRemoveNoteLinkMutation,
  useRequestNoteUploadMutation,
  useCompleteNoteUploadMutation,
  useNoteAttachmentUrlMutation,
  useNoteExportUrlMutation,
  useDeleteNoteAttachmentMutation,
} = notesApi;
